/**
 * useFilterState Hook
 *
 * Manages filter state with immediate URL synchronization and localStorage persistence.
 * Filters apply instantly when changed and persist across page refreshes.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FilterConfig, DateRangeFilterConfig, UseFilterStateReturn, ConductDimension, RegulatoryFlag, FilterLogic } from '../types/filter.types';
import {
  FILTER_PARAM_KEYS,
  FILTER_STORAGE_KEY,
  VALID_DATE_RANGE_PRESETS,
  VALID_DIMENSIONS,
  VALID_REGULATORY_FLAGS,
  VALID_FILTER_LOGIC,
  DEFAULT_FILTER_LOGIC,
} from '../constants/filter.constants';

// =============================================================================
// Storage Functions
// =============================================================================

/**
 * Save filters to localStorage.
 */
function saveFiltersToStorage(filters: FilterConfig): void {
  try {
    if (Object.keys(filters).length === 0) {
      localStorage.removeItem(FILTER_STORAGE_KEY);
    } else {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    }
  } catch {
    // Ignore storage errors (e.g., quota exceeded, private browsing)
  }
}

/**
 * Load filters from localStorage.
 */
function loadFiltersFromStorage(): FilterConfig {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as FilterConfig;
    }
  } catch {
    // Ignore parse errors
  }
  return {};
}

/**
 * Clear persisted filters from localStorage.
 * Call this on logout to reset filter state.
 */
export function clearFilterStorage(): void {
  try {
    localStorage.removeItem(FILTER_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

// =============================================================================
// URL Serialization
// =============================================================================

/**
 * Convert FilterConfig to URL search params.
 * Only includes logic params when they differ from defaults.
 */
function filterConfigToParams(config: FilterConfig): URLSearchParams {
  const params = new URLSearchParams();

  if (config.dateRange?.preset) {
    params.set('dateRangePreset', config.dateRange.preset);
  } else if (
    config.dateRange &&
    !config.dateRange.preset &&
    !config.dateRange.from &&
    !config.dateRange.to
  ) {
    // Present but empty → the user explicitly cleared the range ("all time").
    // Persist a sentinel so this survives a URL round-trip, distinct from
    // "no date filter chosen yet" (which omits all date params).
    params.set('dateRangeCleared', '1');
  }
  if (config.dateRange?.from) {
    params.set('dateFrom', config.dateRange.from);
  }
  if (config.dateRange?.to) {
    params.set('dateTo', config.dateRange.to);
  }

  // Business context filters with logic
  if (config.businessContext?.productCategories?.values?.length) {
    params.set('productCategories', config.businessContext.productCategories.values.join(','));
    if (config.businessContext.productCategories.logic !== DEFAULT_FILTER_LOGIC.productCategories) {
      params.set('productCategoriesLogic', config.businessContext.productCategories.logic);
    }
  }
  if (config.businessContext?.channels?.values?.length) {
    params.set('channels', config.businessContext.channels.values.join(','));
    if (config.businessContext.channels.logic !== DEFAULT_FILTER_LOGIC.channels) {
      params.set('channelsLogic', config.businessContext.channels.logic);
    }
  }
  if (config.businessContext?.journeyStages?.values?.length) {
    params.set('journeyStages', config.businessContext.journeyStages.values.join(','));
    if (config.businessContext.journeyStages.logic !== DEFAULT_FILTER_LOGIC.journeyStages) {
      params.set('journeyStagesLogic', config.businessContext.journeyStages.logic);
    }
  }

  // Dimensions with logic
  if (config.dimensions?.values?.length) {
    params.set('dimensions', config.dimensions.values.join(','));
    if (config.dimensions.logic !== DEFAULT_FILTER_LOGIC.dimensions) {
      params.set('dimensionsLogic', config.dimensions.logic);
    }
  }

  // Regulatory flags with logic
  if (config.regulatoryFlags?.values?.length) {
    params.set('regulatoryFlags', config.regulatoryFlags.values.join(','));
    if (config.regulatoryFlags.logic !== DEFAULT_FILTER_LOGIC.regulatoryFlags) {
      params.set('regulatoryFlagsLogic', config.regulatoryFlags.logic);
    }
  }

  if (config.search) {
    params.set('search', config.search);
  }
  if (config.conductScoreMin !== undefined) {
    params.set('conductScoreMin', String(config.conductScoreMin));
  }
  if (config.conductScoreMax !== undefined) {
    params.set('conductScoreMax', String(config.conductScoreMax));
  }

  return params;
}

/**
 * Validate and filter dimension values from URL params.
 */
function validateDimensions(values: string[]): ConductDimension[] {
  return values.filter((v): v is ConductDimension =>
    (VALID_DIMENSIONS as readonly string[]).includes(v)
  );
}

/**
 * Validate and filter regulatory flag values from URL params.
 */
function validateRegulatoryFlags(values: string[]): RegulatoryFlag[] {
  return values.filter((v): v is RegulatoryFlag =>
    (VALID_REGULATORY_FLAGS as readonly string[]).includes(v)
  );
}

/**
 * Validate date range preset value from URL params.
 */
function validateDateRangePreset(value: string | null): DateRangeFilterConfig['preset'] {
  if (value && (VALID_DATE_RANGE_PRESETS as readonly string[]).includes(value)) {
    return value as DateRangeFilterConfig['preset'];
  }
  return undefined;
}

/**
 * Validate filter logic value from URL params.
 * Returns the default value if the provided value is invalid.
 */
function validateFilterLogic(value: string | null, defaultValue: FilterLogic): FilterLogic {
  if (value && (VALID_FILTER_LOGIC as readonly string[]).includes(value)) {
    return value as FilterLogic;
  }
  return defaultValue;
}

/**
 * Parse FilterConfig from URL search params with validation.
 */
function paramsToFilterConfig(params: URLSearchParams): FilterConfig {
  const config: FilterConfig = {};

  const dateRangePreset = params.get('dateRangePreset');
  const dateFrom = params.get('dateFrom');
  const dateTo = params.get('dateTo');
  const dateRangeCleared = params.get('dateRangeCleared');

  const validatedPreset = validateDateRangePreset(dateRangePreset);
  if (validatedPreset || dateFrom || dateTo) {
    config.dateRange = {
      preset: validatedPreset,
      from: dateFrom || undefined,
      to: dateTo || undefined,
    };
  } else if (dateRangeCleared === '1') {
    // Explicitly-cleared range ("all time / no filter") — an empty object,
    // distinct from an absent dateRange (never chosen).
    config.dateRange = {};
  }

  // Business context filters with logic
  const productCategories = params.get('productCategories');
  const channels = params.get('channels');
  const journeyStages = params.get('journeyStages');

  if (productCategories || channels || journeyStages) {
    config.businessContext = {};

    if (productCategories) {
      const values = productCategories.split(',').filter(Boolean);
      if (values.length > 0) {
        config.businessContext.productCategories = {
          values,
          logic: validateFilterLogic(params.get('productCategoriesLogic'), DEFAULT_FILTER_LOGIC.productCategories),
        };
      }
    }

    if (channels) {
      const values = channels.split(',').filter(Boolean);
      if (values.length > 0) {
        config.businessContext.channels = {
          values,
          logic: validateFilterLogic(params.get('channelsLogic'), DEFAULT_FILTER_LOGIC.channels),
        };
      }
    }

    if (journeyStages) {
      const values = journeyStages.split(',').filter(Boolean);
      if (values.length > 0) {
        config.businessContext.journeyStages = {
          values,
          logic: validateFilterLogic(params.get('journeyStagesLogic'), DEFAULT_FILTER_LOGIC.journeyStages),
        };
      }
    }
  }

  // Dimensions with logic
  const dimensions = params.get('dimensions');
  if (dimensions) {
    const validatedDimensions = validateDimensions(dimensions.split(',').filter(Boolean));
    if (validatedDimensions.length > 0) {
      config.dimensions = {
        values: validatedDimensions,
        logic: validateFilterLogic(params.get('dimensionsLogic'), DEFAULT_FILTER_LOGIC.dimensions),
      };
    }
  }

  // Regulatory flags with logic
  const regulatoryFlags = params.get('regulatoryFlags');
  if (regulatoryFlags) {
    const validatedFlags = validateRegulatoryFlags(regulatoryFlags.split(',').filter(Boolean));
    if (validatedFlags.length > 0) {
      config.regulatoryFlags = {
        values: validatedFlags,
        logic: validateFilterLogic(params.get('regulatoryFlagsLogic'), DEFAULT_FILTER_LOGIC.regulatoryFlags),
      };
    }
  }

  const search = params.get('search');
  if (search) {
    config.search = search;
  }

  const conductScoreMin = params.get('conductScoreMin');
  if (conductScoreMin) {
    const parsed = parseInt(conductScoreMin, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
      config.conductScoreMin = parsed;
    }
  }

  const conductScoreMax = params.get('conductScoreMax');
  if (conductScoreMax) {
    const parsed = parseInt(conductScoreMax, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
      config.conductScoreMax = parsed;
    }
  }

  return config;
}

/**
 * Check if URL has any filter params.
 */
function hasFilterParams(params: URLSearchParams): boolean {
  return (FILTER_PARAM_KEYS as readonly string[]).some((key) => params.has(key));
}

/**
 * Check if a URL parameter key is filter-related.
 */
function isFilterParam(key: string): boolean {
  return (FILTER_PARAM_KEYS as readonly string[]).includes(key);
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook to manage filter state with URL synchronization and localStorage persistence.
 *
 * Features:
 * - Filters apply immediately when changed
 * - URL synchronization for sharing and navigation
 * - localStorage persistence across page refreshes
 * - URL params take priority over stored filters (for deep links)
 * - Preserves non-filter URL params (page, pageSize, etc.)
 *
 * @example
 * ```tsx
 * const { filters, setFilters, reset } = useFilterState();
 *
 * return (
 *   <FilterStrip
 *     filters={filters}
 *     onFiltersChange={setFilters}
 *     onReset={reset}
 *   />
 * );
 * ```
 */
export function useFilterState(): UseFilterStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Track if we've initialized to avoid re-reading on re-renders
  const isInitializedRef = useRef(false);
  const initialFiltersRef = useRef<FilterConfig>({});
  const prevFiltersRef = useRef<FilterConfig>(initialFiltersRef.current);

  // Initialize filters: URL params take priority, then localStorage
  if (!isInitializedRef.current) {
    isInitializedRef.current = true;
    // If URL has filter params, use those (for deep links/sharing)
    if (hasFilterParams(searchParams)) {
      initialFiltersRef.current = paramsToFilterConfig(searchParams);
    } else {
      // Otherwise, load from localStorage
      initialFiltersRef.current = loadFiltersFromStorage();
    }
  }

  const [filters, setFiltersState] = useState<FilterConfig>(initialFiltersRef.current);

  // Update URL and localStorage when filters change
  useEffect(() => {
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
    prevFiltersRef.current = filters;

    // Only sync to URL when filters actually changed — otherwise we overwrite
    // pagination params (page, pageSize) set by other components.
    if (!filtersChanged) return;

    // Save to localStorage
    saveFiltersToStorage(filters);

    // Update URL
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams();

        // Preserve non-filter params
        prev.forEach((value, key) => {
          if (!isFilterParam(key)) {
            next.set(key, value);
          }
        });

        // Add filter params
        const filterParams = filterConfigToParams(filters);
        filterParams.forEach((value, key) => {
          next.set(key, value);
        });

        // Reset page to 1 when filters change
        if (next.has('page')) {
          next.set('page', '1');
        }

        return next;
      },
      { replace: true }
    );
  }, [filters, setSearchParams]);

  // Cross-tab synchronisation via storage event
  useEffect(() => {
    function handleStorageChange(event: StorageEvent) {
      if (event.key !== FILTER_STORAGE_KEY) return;

      try {
        const newFilters: FilterConfig = event.newValue
          ? (JSON.parse(event.newValue) as FilterConfig)
          : {};
        setFiltersState(newFilters);
      } catch {
        // Ignore malformed JSON from other tabs
      }
    }

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Set filters (applies immediately)
  const setFilters = useCallback((newFilters: FilterConfig) => {
    setFiltersState(newFilters);
  }, []);

  // Update a single field in filters
  const updateFilter = useCallback(<K extends keyof FilterConfig>(key: K, value: FilterConfig[K]) => {
    setFiltersState((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Reset all filters to empty
  const reset = useCallback(() => {
    setFiltersState({});
  }, []);

  return {
    filters,
    setFilters,
    updateFilter,
    reset,
  };
}
